import * as React from "react";
import { cn } from "@/lib/utils";

interface TextareaV2Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  errorText?: string;
}

export function TextareaV2({
  label,
  helperText,
  errorText,
  className,
  id,
  "aria-describedby": externalDescribedBy,
  ...props
}: TextareaV2Props) {
  const generatedId = React.useId();
  const textareaId = id ?? generatedId;
  const errorId = errorText ? `${textareaId}-error` : undefined;
  const helperId =
    !errorText && helperText ? `${textareaId}-helper` : undefined;
  const describedBy =
    [externalDescribedBy, errorId, helperId].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className="block space-y-1.5">
      {label ? (
        <label
          htmlFor={textareaId}
          className="text-xs font-semibold text-[var(--v2-text-strong)]"
        >
          {label}
        </label>
      ) : null}
      <textarea
        id={textareaId}
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        className={cn(
          "v2-focus-ring w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] bg-white px-3 py-2 text-sm text-[var(--v2-text-strong)] placeholder:text-slate-400",
          errorText ? "border-red-300" : "",
          className
        )}
        {...props}
      />
      {errorText ? (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {errorText}
        </span>
      ) : null}
      {!errorText && helperText ? (
        <span id={helperId} className="text-xs text-[var(--v2-text-muted)]">
          {helperText}
        </span>
      ) : null}
    </div>
  );
}
